import React, { useEffect, useState, useRef } from "react";
import TimesheetRow from "../Components/Timesheet/TimesheetRow";
import TimesheetDetails from "../Components/Timesheet/TimesheetDetails";
import Sidebar from "../Components/Timesheet/Sidebar";
import LocationMultiSelect from "../Components/Calendar/LocationSelect";

import api from "../Utils/api";

import { isBefore } from "date-fns";
import { toast } from "react-toastify";
import DateRangePicker from "../Components/Timesheet/DateRangePicker";
import Spinner from "../Components/Spinner";

const sortFields = [
  { label: "First Name", value: "preferredName" },
  { label: "Last Name", value: "lastName" },
  // { label: "Total Hours", value: "totalHours" },
  // Add more as needed
];

export default function Approve() {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [period, setPeriod] = useState(7);

  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [locations, setLocations] = useState([]);

  const [filteredLocations, setFilteredLocations] = useState([]);

  const [selectedUserID, setSelecteUserID] = useState("");
  const [selectedScheduleID, setSelectedScheduleID] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("preferredName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const getDaysBetween = (start, end) => {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    getUsers();
    getLocations();
    getSchedules(period);
  }, []);

  useEffect(() => {
    let len = schedules.filter(
      (sc) => sc.userID?._id === selectedUserID
    ).length;
    if (len == 0) {
      setSelectedScheduleID("");
    }
  }, [selectedUserID, schedules]);

  useEffect(() => {
    if (isBefore(endDate, startDate)) {
      toast.info("End date cannot be before start date");
      setEndDate(startDate);
      return;
    }
    let period = getDaysBetween(startDate, endDate);
    getSchedules(period);
  }, [startDate, endDate]);

  const getUsers = async () => {
    try {
      const response = await api.get("/user");
      setUsers(response.data.users.filter((u) => !u.archive));
    } catch (error) {
      console.error(error);
    }
  };

  const getLocations = async () => {
    try {
      const response = await api.get("/location");
      setLocations(response.data.locations.filter((loc) => !loc.archive));
    } catch (error) {
      console.error(error);
    }
  };

  const getSchedules = async (period) => {
    function formatLocalDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    try {
      setIsLoading(true);
      const startDateString = formatLocalDate(startDate);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + period);
      const endDateString = formatLocalDate(endDate);

      const response = await api.post(`/schedule/list`, {
        startTime: startDateString,
        endTime: endDateString,
      });
      setIsLoading(false);
      setSchedules(response.data.schedules);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDateChange = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleUserSelect = (userID) => {
    setSelecteUserID(userID);
  };

  const handleScheduleSelect = (scheduleID) => {
    setSelectedScheduleID(scheduleID);
  };

  const reloadTimesheets = () => {
    getSchedules(period);
  };

  useEffect(() => {
    let firstUser = filteredAndSortedUsers(users)[0];
    if (firstUser) setSelecteUserID(firstUser?._id);
  }, [users]);

  const filteredAndSortedUsers = () => {
    let filtered = users.filter((user) =>
      user.preferredName.toLowerCase().includes(searchTerm.toLowerCase())
    );
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (aValue === undefined || bValue === undefined) return 0;
      if (typeof aValue === "string") {
        return sortOrder === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      // For numbers
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
    return filtered;
  };

  return (
    <div className="flex flex-wrap px-6">
      {isLoading && <Spinner />}
      <div className="w-full mb-4 mt-4 flex align-center justify-start">
        <LocationMultiSelect
          setFilteredLocations={setFilteredLocations}
          locations={locations.map((item) => item.name)}
        />
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
      </div>
      <hr className="w-full mb-8" />
      <div>
        <div className="flex  w-[200px] mb-2">
          <input
            className="border w-56 mr-2 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-gray-300"
            value={searchTerm}
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Sort Dropdown Button */}
          <div className="relative">
            <button
              className="border rounded px-2 py-1 bg-white flex items-center gap-1"
              onClick={() => setDropdownOpen((open) => !open)}
            >
              <span className="hidden sm:inline">Sort</span>
            </button>
            {dropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white border rounded-lg shadow-lg z-10">
                {sortFields.map((field) => (
                  <div
                    key={field.value}
                    className={`flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                      sortField === field.value ? "font-bold text-gray-700" : ""
                    }`}
                    onClick={() => {
                      setSortField(field.value);
                      setDropdownOpen(false);
                    }}
                  >
                    {field.label}
                    {sortField === field.value && (
                      <span
                        className="ml-auto cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSortOrder((order) =>
                            order === "asc" ? "desc" : "asc"
                          );
                        }}
                      >
                        {sortOrder === "asc" ? "↑" : "↓"}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <Sidebar
          users={filteredAndSortedUsers()}
          schedules={schedules.filter((schedule) =>
            filteredLocations.some(
              (targetName) =>
                schedule.locationID &&
                schedule.locationID.name &&
                schedule.locationID.name.trim() === targetName.trim()
            )
          )}
          selectedUserID={selectedUserID}
          onSelect={handleUserSelect}
        />
      </div>
      <div className="bg-white w-[calc(100%-16rem)]">
        <TimesheetRow
          onSelect={handleScheduleSelect}
          selectedScheduleID={selectedScheduleID}
          schedules={schedules
            .filter((schedule) =>
              filteredLocations.some(
                (targetName) =>
                  schedule.locationID &&
                  schedule.locationID.name &&
                  schedule.locationID.name.trim() === targetName.trim()
              )
            )
            .filter((sc) => sc.userID?._id === selectedUserID)}
        />
        <TimesheetDetails
          locations={locations}
          reloadTimesheets={reloadTimesheets}
          schedule={schedules.find((sc) => sc._id == selectedScheduleID)}
        />
      </div>
    </div>
  );
}
